
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { GymNameField } from "./form/GymNameField";
import { GymAddressField } from "./form/GymAddressField";
import { GymCodeDisplay } from "./form/GymCodeDisplay";
import { GymLogoUpload } from "./form/GymLogoUpload";
import { ManagementSection } from "./form/ManagementSection";
import { DeleteGymButton } from "./form/DeleteGymButton";
import { toast } from "@/hooks/use-toast";
import { Pencil, Trash2, Loader2 } from "lucide-react";

interface GymFormProps {
  gymId: string;
  gymName: string;
  gymAddress: string;
  gymCode?: string;
  gymLogoUrl?: string;
  isEditing: boolean;
  onSave: (name: string, address: string, logoFile: File | null) => void | Promise<void>;
  onCancel: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export const GymForm = ({
  gymId,
  gymName: initialGymName,
  gymAddress: initialGymAddress,
  gymCode,
  gymLogoUrl,
  isEditing,
  onSave,
  onCancel,
  onEdit,
  onDelete,
}: GymFormProps) => {
  const [tempGymName, setTempGymName] = useState(initialGymName);
  const [tempGymAddress, setTempGymAddress] = useState(initialGymAddress);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Sincronizar estado cuando los props cambien
  useEffect(() => {
    setTempGymName(initialGymName);
  }, [initialGymName]);

  useEffect(() => {
    setTempGymAddress(initialGymAddress);
  }, [initialGymAddress]);

  // Resetear logoFile cuando se salga del modo edición
  useEffect(() => {
    if (!isEditing) {
      setLogoFile(null);
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (!tempGymName.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "El nombre del gimnasio es obligatorio.",
      });
      return;
    }

    if (!tempGymAddress.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "La dirección del gimnasio es obligatoria.",
      });
      return;
    }

    setIsSaving(true);
    try {
      await onSave(tempGymName.trim(), tempGymAddress.trim(), logoFile);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTempGymName(initialGymName);
    setTempGymAddress(initialGymAddress);
    setLogoFile(null);
    onCancel();
  };

  return (
    <div className="space-y-5">
      <div className="space-y-4 p-4 rounded-xl bg-card/30 border border-border/30">
        <GymNameField
          gymName={tempGymName}
          setGymName={setTempGymName}
          isEditing={isEditing}
        />
        <GymAddressField
          gymAddress={tempGymAddress}
          setGymAddress={setTempGymAddress}
          isEditing={isEditing}
        />
        <GymLogoUpload
          currentLogoUrl={gymLogoUrl}
          onLogoChange={setLogoFile}
          isEditing={isEditing}
        />
        
        {!isEditing && (
          <div className="flex gap-2 pt-3 border-t border-border/30">
            <Button
              variant="outline"
              onClick={onEdit}
              className="flex-1 bg-background/50 hover:bg-background border-border/50"
            >
              <Pencil className="mr-2 h-4 w-4" />
              Editar
            </Button>
            <DeleteGymButton onDelete={onDelete} />
          </div>
        )}
      </div>
      
      {isEditing ? (
        <div className="flex gap-3">
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 bg-primary hover:bg-primary/90 shadow-sm"
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              "Guardar"
            )}
          </Button>
          <Button
            onClick={handleCancel}
            variant="outline"
            disabled={isSaving}
            className="flex-1 bg-background/50 hover:bg-background border-border/50"
          >
            Cancelar
          </Button>
        </div>
      ) : (
        <ManagementSection gymId={gymId} />
      )}
      
      {gymCode && <GymCodeDisplay code={gymCode} gymId={gymId} />}
    </div>
  );
};
